import { DatePipe } from '@angular/common';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { CompanyFlagsCardListItemComponent } from '@nk/company-block';
import {
  CompanyEntity,
  CompanyFlagTypeEntity,
  CustomerPersonEntity,
  MonitoringOriginEntityAggregateEntity,
  MonitoringOriginEntityComposite,
  PersonEntity,
  SimpleCollection,
} from '@nk/data-entities-lib';
import {
  CardListItemLinkComponent,
  CardListItemTextComponent,
  NkCardListConfig,
  NkCardListData,
  NkCardListItemLink,
  orderByToCardListSorts,
} from '@nk/ui-lib';
import { DateFormats, OrderBy, swapKeysAndValues } from '@nk/utils-lib';
import { ScreeningCaseCardStatusListItemComponent } from '../components/screening-case-card-status-list-item/screening-case-card-status-list-item.component';
import { getScreeningStatusTags, ScreeningStatusTag } from './screening-overview.utils';

export interface MonitoringOriginEntityCompositeCardListItem {
  uuid: string;
  monitoringOriginEntity: NkCardListItemLink;
  screeningStatusTags: ScreeningStatusTag[];
  screeningCaseStateUpdateTimestamp: string;
  companyFlags: CompanyFlagTypeEntity[];
  monitoringConnectionCount: string;
}

export const MONITORING_ORIGIN_ENTITY_ORDER_BY_MAPPING_DICT: Partial<
  Record<keyof MonitoringOriginEntityCompositeCardListItem, string>
> = {
  screeningStatusTags: 'monitoring_origin_entity_aggregate.pending_screening_actions_flag',
  screeningCaseStateUpdateTimestamp: 'monitoring_origin_entity_aggregate.screening_case_latest_update_timestamp',
};

export const MONITORING_ORIGIN_ENTITY_COMPOSITE_CARD_LIST_CONFIG: NkCardListConfig<MonitoringOriginEntityCompositeCardListItem> =
  {
    items: [
      {
        andClass: 'col-sm-3',
        label: extractTranslation('screening.monitoring_origin_entity.table.company_name'),
        prop: 'monitoringOriginEntity',
        component: CardListItemLinkComponent,
        sortable: false,
      },
      {
        andClass: 'col-sm-3',
        label: extractTranslation('screening.monitoring_origin_entity.table.screening_status_tags'),
        prop: 'screeningStatusTags',
        component: ScreeningCaseCardStatusListItemComponent,
        sortable: true,
      },
      {
        andClass: 'col-sm-3',
        label: extractTranslation('screening.monitoring_origin_entity.table.screening_case_state_update_timestamp'),
        prop: 'screeningCaseStateUpdateTimestamp',
        component: CardListItemTextComponent,
        sortable: true,
      },
      {
        andClass: 'col-sm-2',
        label: extractTranslation('screening.monitoring_origin_entity.table.company_flags'),
        prop: 'companyFlags',
        component: CompanyFlagsCardListItemComponent,
        sortable: false,
      },
      {
        andClass: 'col-sm-1',
        label: extractTranslation('screening.monitoring_origin_entity.table.monitoring_connection_count'),
        prop: 'monitoringConnectionCount',
        component: CardListItemTextComponent,
        sortable: false,
      },
    ],
    noDataMessage: extractTranslation('screening.monitoring_origin_entity.table.no_data_found'),
  };

export function getMonitoringOriginEntityScreeningStatusTags(
  aggregate: MonitoringOriginEntityAggregateEntity,
): ScreeningStatusTag[] {
  return getScreeningStatusTags(aggregate);
}

export function mapToMonitoringOriginEntityCompositeCardListItems(
  collection: SimpleCollection<MonitoringOriginEntityComposite>,
  currentPage: number,
  datePipe: DatePipe,
  pageLimit: number,
  orderBy: OrderBy[],
): NkCardListData<MonitoringOriginEntityCompositeCardListItem> {
  const cardListItems: MonitoringOriginEntityCompositeCardListItem[] = collection.elements.map((composite) => ({
    uuid: composite.uuid,
    monitoringOriginEntity: {
      routerLink: getRouterLink(composite),
      label: getEntityName(composite),
      subLabel: getEntitySubLabel(composite, datePipe),
    },
    screeningStatusTags: getMonitoringOriginEntityScreeningStatusTags(composite.monitoring_origin_entity_aggregate),
    screeningCaseStateUpdateTimestamp: getScreeningCaseStateUpdateTimestamp(
      composite.monitoring_origin_entity_aggregate.screening_case_latest_update_timestamp,
      datePipe,
    ),
    companyFlags: composite.company_flag_history_composites.map((i) => i.company_flag_type),
    monitoringConnectionCount: composite.monitoring_origin_entity_aggregate.monitoring_connection_count.toString(),
    companyFlagHistoryComposites: composite.company_flag_history_composites,
  }));
  const reversedOrderByMapping = swapKeysAndValues(MONITORING_ORIGIN_ENTITY_ORDER_BY_MAPPING_DICT);
  const sorts = orderByToCardListSorts<MonitoringOriginEntityCompositeCardListItem>(
    orderBy || [],
    reversedOrderByMapping,
  );
  return {
    entities: cardListItems,
    pagination: {
      limit: pageLimit,
      page: currentPage,
      totalCount: collection.count,
    },
    sorts,
  };
}

export function getRouterLink(composite: MonitoringOriginEntityComposite): string[] {
  if (composite.company && composite.company.uuid) {
    return ['/company', composite.company.uuid, 'screening'];
  }
  if (composite.customer_person && composite.customer_person.uuid) {
    return ['/screening', 'customer-person', composite.customer_person.uuid];
  }
  return [''];
}

export function getEntityName<
  T extends {
    person?: PersonEntity | null;
    company?: CompanyEntity | null;
    customer_person?: CustomerPersonEntity | null;
  },
>(composite: T): string {
  if (composite.person) {
    return composite.person.full_name;
  }
  if (composite.company) {
    return composite.company.name;
  }
  if (composite.customer_person) {
    return composite.customer_person.full_name;
  }
  // Should never happen
  return extractTranslation('common.unknown');
}

export function getEntitySubLabel(entity: MonitoringOriginEntityComposite, datePipe: DatePipe): string {
  if (entity.company) {
    return entity.company.org_nr || '';
  }
  if (entity.customer_person) {
    return entity.customer_person.birth_date
      ? datePipe.transform(entity.customer_person.birth_date, DateFormats.ShortFormFullDate) || ''
      : '';
  }
  return '';
}

export function getScreeningCaseStateUpdateTimestamp(date: Date | null, datePipe: DatePipe): string {
  return date ? datePipe.transform(date, DateFormats.DateAndTime) || '-' : '-';
}
